import { updatePassword } from "aws-amplify/auth";
import React, {
	FormEvent,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { meetsPasswordRequirements } from "../../helper/password-requirements";
import IconDone from "../../dynamicicons/icon-done";
import VelavuButton from "../../elements/velavu-button";
import VelavuInput from "../../elements/velavu-input";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import * as styles from "./change-password-modal.module.scss";
import whitelabel from "../../whitelabel";
import VelavuNoticeHeader from "../../elements/velavu-notice/velavu-notice-header";
import VelavuNoticeBody from "../../elements/velavu-notice/velavu-notice-body";
import VelavuModalContainer from "../../elements/velavu-modal-container";

export default function ChangePasswordModal(props: { onClose: VoidFunction }) {
	const { t } = useTranslation();
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

	const [newPasswordError, setNewPasswordError] = useState(false);
	const [newPasswordConfirmError, setNewPasswordConfirmError] =
		useState(false);

	const [isLoading, setLoading] = useState(false);
	const [result, setResult] = useState<
		{ success: boolean; description: string } | undefined
	>(undefined);

	const currentPasswordOK = currentPassword.length > 0;
	const newPasswordOK = meetsPasswordRequirements(newPassword);
	const newPasswordOKDisplay = newPassword.length === 0 || newPasswordOK;
	const newPasswordConfirmOK =
		newPasswordConfirm.length > 0 && newPassword === newPasswordConfirm;
	const newPasswordConfirmOKDisplay =
		newPasswordConfirm.length === 0 || newPasswordConfirmOK;

	const submitPassword = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			//Ignoring if the state is invalid
			if (
				!(currentPasswordOK && newPasswordOK && newPasswordConfirmOK) ||
				isLoading
			)
				return;

			//Setting the view as loading
			setLoading(true);

			//Submitting the password change
			updatePassword({
				oldPassword: currentPassword,
				newPassword: newPassword,
			})
				.then(() => {
					setResult({
						success: true,
						description: t("settings.change_password.success_body"),
					});
				})
				.catch((error) => {
					console.log(error);
					setResult({ success: false, description: error.message });
				})
				.finally(() => setLoading(false));
		},
		[
			currentPasswordOK,
			newPasswordOK,
			newPasswordConfirmOK,
			isLoading,
			currentPassword,
			newPassword,
			t,
		],
	);

	const checkNewPasswordError = useCallback(() => {
		//Set new password error on blur
		if (!newPasswordOKDisplay) setNewPasswordError(true);
	}, [newPasswordOKDisplay, setNewPasswordError]);

	useEffect(() => {
		//Clear new password error on edit
		if (newPasswordError && newPasswordOKDisplay) {
			setNewPasswordError(false);
		}
	}, [newPasswordError, newPasswordOKDisplay, setNewPasswordError]);

	const checkNewPasswordConfirmError = useCallback(() => {
		//Set new password confirm error on blur
		if (!newPasswordConfirmOKDisplay) setNewPasswordConfirmError(true);
	}, [newPasswordConfirmOKDisplay, setNewPasswordConfirmError]);

	useEffect(() => {
		//Clear new password confirm error on edit
		if (newPasswordConfirmError && newPasswordConfirmOKDisplay) {
			setNewPasswordConfirmError(false);
		}
	}, [
		newPasswordConfirmError,
		newPasswordConfirmOKDisplay,
		setNewPasswordConfirmError,
	]);

	return (
		<VelavuModalContainer>
			<form className={styles.container} onSubmit={submitPassword}>
				<span className={styles.title}>
					{t("settings.change_password.title")}
				</span>
				<VelavuInput
					value={currentPassword}
					onChange={setCurrentPassword}
					type="password"
					titleText={t(
						"settings.change_password.current_password_label",
					)}
					placeholder={t(
						"settings.change_password.current_password_placeholder",
					)}
					autoComplete="current-password"
					disabled={isLoading}
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					value={newPassword}
					onChange={setNewPassword}
					onBlur={checkNewPasswordError}
					type="password"
					titleText={t("settings.change_password.new_password_label")}
					placeholder={t(
						"settings.change_password.new_password_placeholder",
					)}
					autoComplete="new-password"
					disabled={isLoading}
					error={newPasswordError}
					trailingIcon={
						newPasswordOK ? (
							<IconDone
								color={whitelabel.palette.active500}
								size={16}
							/>
						) : undefined
					}
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					value={newPasswordConfirm}
					onChange={setNewPasswordConfirm}
					onBlur={checkNewPasswordConfirmError}
					type="password"
					titleText={t(
						"settings.change_password.confirm_password_label",
					)}
					placeholder={t(
						"settings.change_password.confirm_password_placeholder",
					)}
					autoComplete="new-password"
					disabled={isLoading}
					error={newPasswordConfirmError}
					trailingIcon={
						newPasswordConfirmOK ? (
							<IconDone
								color={whitelabel.palette.active500}
								size={16}
							/>
						) : undefined
					}
					fullWidth
					bottomPadding
				/>

				<VelavuNotice type="info">
					{t("password_requirements")}
				</VelavuNotice>

				<div className={styles.buttonBar}>
					<VelavuButton
						label={t("settings.change_password.cancel")}
						onClick={props.onClose}
						variant="outlined"
						fullWidth
						submit={false}
					/>

					<div className={styles.buttonBarSpacer} />

					<VelavuButton
						label={t("settings.change_password.save")}
						disabled={
							!(
								currentPasswordOK &&
								newPasswordOK &&
								newPasswordConfirmOK
							) || isLoading
						}
						fullWidth
						submit
					/>
				</div>

				{result && (
					<VelavuNotice
						className={styles.statusNotice}
						type={result.success ? "confirm" : "error"}
					>
						<VelavuNoticeHeader>
							{result.success
								? t("settings.change_password.success_title")
								: t("settings.change_password.failed_title")}
						</VelavuNoticeHeader>

						<VelavuNoticeBody>
							{result.description}
						</VelavuNoticeBody>
					</VelavuNotice>
				)}
			</form>
		</VelavuModalContainer>
	);
}
